import { useTracks, VideoTrack, useParticipants } from "@livekit/components-react";
import { Track, VideoQuality, type Participant } from "livekit-client";
import { useState, useEffect, useCallback } from "react";

interface VideoGridProps {
  hostIdentity: string;
  raisedIdentities: Set<string>;
  mutedByHost: Record<string, { mic: boolean; cam: boolean }>;
}

function NameBadge({
  participant,
  isHost,
  raised,
  onClick,
  isFocused,
  micMuted,
  mutedByHost,
}: {
  participant: Participant;
  isHost: boolean;
  raised: boolean;
  onClick?: () => void;
  isFocused?: boolean;
  micMuted: boolean;
  mutedByHost: Record<string, { mic: boolean; cam: boolean }>;
}) {
  const name = participant.name || participant.identity || "Katılımcı";
  const base = isFocused
    ? "absolute bottom-0 left-0 right-0 px-3 py-2 flex items-center gap-2 bg-gradient-to-t from-black/80 via-black/50 to-transparent"
    : "px-1.5 py-1 text-center bg-background-900 border-t border-background-700/40";

  return (
    <div className={base} onClick={onClick} role={onClick ? "button" : undefined}>
      <div className="flex items-center gap-1.5 min-w-0 justify-center">
        {raised && !isFocused && (
          <span className="text-accent-400 text-xs shrink-0">
            <i className="fa-solid fa-hand" aria-hidden="true"></i>
          </span>
        )}
        <span className={`truncate font-semibold ${isFocused ? "text-white text-sm" : "text-background-200 text-[11px]"}`}>
          {name}
        </span>
        {isHost && (
          <span className={`shrink-0 px-1 py-0 rounded bg-primary-500 text-white font-bold ${isFocused ? "text-[10px]" : "text-[9px]"}`}>
            Öğretmen
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className={`w-5 h-5 flex items-center justify-center rounded-full ${micMuted ? "bg-red-500/90" : "bg-green-500/90"}`}>
          <i className={`text-[10px] text-white ${micMuted ? "ri-mic-off-line" : "ri-mic-line"}`} aria-hidden="true"></i>
        </span>
        {mutedByHost[participant.identity]?.mic === false && !isHost && (
          <span className="px-1 py-0.5 rounded bg-red-500/90 text-white text-[9px] font-semibold">Sessiz</span>
        )}
      </div>
    </div>
  );
}

export default function VideoGrid({ hostIdentity, raisedIdentities, mutedByHost }: VideoGridProps) {
  const videoTracks = useTracks(
    [Track.Source.Camera, Track.Source.ScreenShare],
    { onlySubscribed: true },
  );
  const participants = useParticipants();
  const [focusId, setFocusId] = useState<string>(hostIdentity);
  const [screenShare, setScreenShare] = useState<typeof videoTracks[0] | null>(null);

  useEffect(() => {
    const ss = videoTracks.find((t) => t.source === Track.Source.ScreenShare) || null;
    setScreenShare(ss);
  }, [videoTracks]);

  useEffect(() => {
    const allIds = new Set(participants.map((p) => p.identity));
    if (!allIds.has(focusId)) {
      setFocusId(hostIdentity);
    }
  }, [participants, focusId, hostIdentity]);

  const setQuality = useCallback((participantId: string, quality: VideoQuality) => {
    const camTrack = videoTracks.find(
      (t) => t.participant.identity === participantId && t.source === Track.Source.Camera,
    );
    if (camTrack && camTrack.publication) {
      try {
        (camTrack.publication as unknown as { setVideoQuality?: (q: VideoQuality) => void }).setVideoQuality?.(quality);
      } catch {
        // ignore if API not available
      }
    }
  }, [videoTracks]);

  useEffect(() => {
    participants.forEach((p) => {
      setQuality(p.identity, p.identity === focusId ? VideoQuality.HIGH : VideoQuality.MEDIUM);
    });
  }, [participants, focusId, setQuality]);

  if (participants.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-800 text-background-400">
          <i className="ri-video-upload-line text-3xl" aria-hidden="true"></i>
        </span>
        <p className="text-sm text-background-400">Katılımcılar bağlanıyor…</p>
      </div>
    );
  }

  const focusedParticipant = participants.find((p) => p.identity === focusId) || participants[0];
  const focusedIsHost = focusedParticipant.identity === hostIdentity;
  const focusedRaised = raisedIdentities.has(focusedParticipant.identity);
  const focusedMicPub = focusedParticipant.getTrackPublication(Track.Source.Microphone);
  const focusedMicMuted = !focusedMicPub || focusedMicPub.isMuted;

  const sidebarParticipants = participants.filter((p) => p.identity !== focusId);

  const focusedCamTrack = videoTracks.find(
    (t) => t.participant.identity === focusId && t.source === Track.Source.Camera,
  );

  const focusedName = focusedParticipant.name || focusedParticipant.identity || "Katılımcı";

  return (
    <div className="h-full flex gap-2">
      {/* ===== Main stage (left) ===== */}
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        {screenShare && (
          <div className="relative rounded-xl overflow-hidden bg-background-900 flex-none h-[45%] border border-background-700/30">
            <VideoTrack trackRef={screenShare} className="w-full h-full object-contain" />
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-bold shadow-lg flex items-center gap-2">
              <i className="ri-computer-line" aria-hidden="true"></i>
              Ekran Paylaşımı — {screenShare.participant.name || screenShare.participant.identity}
            </div>
          </div>
        )}

        <div className="flex-1 relative rounded-xl overflow-hidden bg-background-900 border border-background-700/30 min-h-0">
          {focusedCamTrack ? (
            <>
              <VideoTrack trackRef={focusedCamTrack} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-lg bg-black/50 text-white text-[10px] font-semibold border border-white/10">
                  HD
                </span>
                {focusedIsHost && (
                  <span className="px-2.5 py-1 rounded-lg bg-primary-500/90 text-white text-[10px] font-bold">
                    Öğretmen
                  </span>
                )}
                {focusedRaised && (
                  <span className="px-2.5 py-1 rounded-lg bg-accent-500/90 text-white text-[10px] font-bold flex items-center gap-1">
                    <i className="fa-solid fa-hand" aria-hidden="true"></i> El Kaldırdı
                  </span>
                )}
              </div>
              <NameBadge
                participant={focusedParticipant}
                isHost={focusedIsHost}
                raised={focusedRaised}
                micMuted={focusedMicMuted}
                mutedByHost={mutedByHost}
                isFocused
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-background-700 flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl font-bold text-background-300">{focusedName[0].toUpperCase()}</span>
                </div>
                <p className="text-sm font-semibold text-background-300">{focusedName}</p>
                <p className="text-xs text-background-500 mt-1">Kamera kapalı</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Sidebar (right) ===== */}
      {sidebarParticipants.length > 0 && (
        <div className="w-44 sm:w-52 lg:w-60 shrink-0 flex flex-col gap-2 overflow-y-auto pr-1">
          {sidebarParticipants.map((participant) => {
            const isHost = participant.identity === hostIdentity;
            const raised = raisedIdentities.has(participant.identity);
            const camTrack = videoTracks.find(
              (t) => t.participant.identity === participant.identity && t.source === Track.Source.Camera,
            );
            const micPub = participant.getTrackPublication(Track.Source.Microphone);
            const micMuted = !micPub || micPub.isMuted;

            return (
              <button
                key={participant.identity}
                type="button"
                onClick={() => setFocusId(participant.identity)}
                className="group relative shrink-0 rounded-lg overflow-hidden bg-background-900 border border-background-700/30 hover:border-primary-400/50 transition-colors text-left"
                style={{ aspectRatio: "16/9", maxHeight: "120px" }}
                title={participant.name || participant.identity}
              >
                {camTrack ? (
                  <>
                    <VideoTrack trackRef={camTrack} className="w-full h-full object-cover" />
                    {isHost && (
                      <div className="absolute top-1.5 left-1.5">
                        <span className="px-1.5 py-0.5 rounded bg-primary-500/90 text-white text-[9px] font-bold">
                          Öğretmen
                        </span>
                      </div>
                    )}
                    {raised && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500/90 text-white">
                          <i className="fa-solid fa-hand text-[10px]" aria-hidden="true"></i>
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-semibold bg-black/50 px-2 py-1 rounded-lg transition-opacity">
                        Büyüt
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-background-700 flex items-center justify-center">
                      <span className="text-base font-bold text-background-300">
                        {(participant.name || "?")[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
                <NameBadge
                  participant={participant}
                  isHost={isHost}
                  raised={raised}
                  micMuted={micMuted}
                  mutedByHost={mutedByHost}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}